import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EyeIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyPoundIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PrinterIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import BusinessLayout from '../../components/layout/BusinessLayout';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { buildApiUrl } from '../../config/api';
import { formatPrice } from '../../utils/formatting';

interface Booking {
  id: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  totalAmount: number;
  bookingDate: string;
  createdAt: string;
  paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded';
  activity: {
    id: string;
    title: string;
    date: string;
    time: string;
  };
  venue: {
    id: string;
    name: string;
  };
  child: {
    id: string;
    firstName: string;
    lastName: string;
  };
  parent: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const BookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      
      const response = await fetch(buildApiUrl('/business/bookings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      if (data.success) {
        setBookings(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowViewModal(true);
  };

  const handleViewAsParent = (booking: Booking) => {
    // Open parent booking flow in new tab
    window.open(`/bookings/flow/${booking.activity.id}`, '_blank');
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status?: string) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-purple-100 text-purple-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'N/A'}
      </span>
    );
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.child.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.child.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.parent.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const matchesPaymentStatus = paymentStatusFilter === 'all' || booking.paymentStatus === paymentStatusFilter;

    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  if (loading) {
    return (
      <BusinessLayout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00806a]"></div>
        </div>
      </BusinessLayout>
    );
  }

  return (
    <BusinessLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Bookings Management</h1>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => fetchBookings()}>
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Summary (moved to top) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 h-36 md:h-40">
            <div className="flex items-center h-full">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Confirmed</p>
                <p className="text-3xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 h-36 md:h-40">
            <div className="flex items-center h-full">
              <ClockIcon className="h-10 w-10 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-3xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 h-36 md:h-40">
            <div className="flex items-center h-full">
              <XCircleIcon className="h-10 w-10 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cancelled</p>
                <p className="text-3xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'cancelled').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 h-36 md:h-40">
            <div className="flex items-center h-full">
              <CurrencyPoundIcon className="h-10 w-10 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatPrice(bookings
                    .filter(b => b.paymentStatus === 'paid')
                    .reduce((sum, b) => sum + b.totalAmount, 0)
                  )}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search bookings..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>

          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
          >
            <option value="all">All Payment Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        {/* Bookings Grid (Square Cards) */}
        <div>
          {filteredBookings.length === 0 ? (
            <Card>
              <div className="px-6 py-12 text-center text-gray-500">No bookings found</div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredBookings.map((booking) => (
                <Card key={booking.id} className="p-5 h-[300px] flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <h3 className="text-base font-semibold text-gray-900 line-clamp-2 pr-2">{booking.activity.title}</h3>
                      <div className="shrink-0">{getStatusBadge(booking.status)}</div>
                    </div>
                    <div className="text-sm text-gray-600">{booking.venue.name}</div>
                    <div className="text-sm text-gray-600 flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                      <span>{new Date(booking.activity.date).toLocaleDateString()}</span>
                      <span className="text-gray-400">•</span>
                      <span>{booking.activity.time}</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Child:</span> {booking.child.firstName} {booking.child.lastName}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{booking.parent.email}</div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-lg font-semibold text-gray-900">{formatPrice(booking.totalAmount)}</div>
                    <div className="flex items-center space-x-3">
                      <span>{getPaymentStatusBadge(booking.paymentStatus)}</span>
                      <button
                        onClick={() => handleViewBooking(booking)}
                        className="text-[#00806a] hover:text-[#006d5a]"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleViewAsParent(booking)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View as Parent"
                      >
                        <UserGroupIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* View Modal */}
        {showViewModal && selectedBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Booking Details</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Activity Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Activity:</span> {selectedBooking.activity.title}</p>
                    <p><span className="font-medium">Venue:</span> {selectedBooking.venue.name}</p>
                    <p><span className="font-medium">Date:</span> {new Date(selectedBooking.activity.date).toLocaleDateString()}</p>
                    <p><span className="font-medium">Time:</span> {selectedBooking.activity.time}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Participant Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Child:</span> {selectedBooking.child.firstName} {selectedBooking.child.lastName}</p>
                    <p><span className="font-medium">Parent:</span> {selectedBooking.parent.firstName} {selectedBooking.parent.lastName}</p>
                    <p><span className="font-medium">Email:</span> {selectedBooking.parent.email}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Booking Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Booking ID:</span> {selectedBooking.id}</p>
                    <p><span className="font-medium">Status:</span> {getStatusBadge(selectedBooking.status)}</p>
                    <p><span className="font-medium">Payment Status:</span> {getPaymentStatusBadge(selectedBooking.paymentStatus)}</p>
                    <p><span className="font-medium">Amount:</span> {formatPrice(selectedBooking.totalAmount)}</p>
                    <p><span className="font-medium">Booked:</span> {new Date(selectedBooking.createdAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => handleViewAsParent(selectedBooking)}
                >
                  View as Parent
                </Button>
                <Button onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </BusinessLayout>
  );
};

export default BookingsPage;
